import React from 'react';
import { Timer, Zap, Navigation, Gauge } from 'lucide-react';
import { CalcTarget, DistanceUnit, PaceOrSpeedMode, Language } from '../../types';
import { themeMap } from '../../utils/calcTheme';
import { t } from '../../utils/i18n';
import { formatTime, formatPace } from '../../utils/calculator';

interface HeroResultCardProps {
  activeCalculation: CalcTarget;
  language: Language;
  distanceKm: number;
  totalSeconds: number;
  paceSecsPerKm: number;
  distanceUnit: DistanceUnit;
  paceOrSpeedMode: PaceOrSpeedMode;
  displayDistance: number;
  displayDistanceUnit: string;
  displayPaceSecs: number;
  displayPaceUnit: string;
  displaySpeed: number;
  displaySpeedUnit: string;
}

/**
 * 1. Resultaatkaart (Actieve berekening, Output)
 * Krijgt een volledige, zachte achtergrondkleur afhankelijk van activeCalculation:
 * - Tijd: bg-orange-100 border-orange-200 text-orange-900
 * - Pace: bg-blue-100 border-blue-200 text-blue-900
 * - Afstand: bg-emerald-100 border-emerald-200 text-emerald-900
 */
export default function HeroResultCard({
  activeCalculation,
  language,
  totalSeconds,
  distanceUnit,
  paceOrSpeedMode,
  displayDistance,
  displayDistanceUnit,
  displayPaceSecs,
  displayPaceUnit,
  displaySpeed,
  displaySpeedUnit,
}: HeroResultCardProps) {
  const currentTheme = themeMap[activeCalculation].resultCard;

  if (activeCalculation === 'time') {
    return (
      <div
        id="hero-result-time"
        className={`rounded-2xl sm:rounded-3xl p-5 sm:p-6 transition-all ${currentTheme.container}`}
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${currentTheme.iconContainer}`}>
            <Timer className="w-5 h-5 stroke-[2.5]" />
          </div>
          <div>
            <span className="text-xs sm:text-xs font-extrabold uppercase tracking-wider block opacity-95">
              {t(language, 'calculatedTime')}
            </span>
            <span className="text-xs opacity-75 font-medium">
              {t(language, 'uiTargetTimeBasedOnDistance')}
            </span>
          </div>
        </div>

        <div className="my-4 text-center py-2.5 bg-white/60 dark:bg-black/25 rounded-2xl">
          <p className={`font-mono font-black text-3xl sm:text-4xl tracking-tight ${currentTheme.number}`}>
            {formatTime(totalSeconds, true)}
          </p>
          <p className="text-xs font-bold mt-0.5 opacity-80">
            {t(language, 'uiHrMinSec')}
          </p>
        </div>

        <div className="flex items-center justify-between text-xs opacity-90 font-medium px-1">
          <span>
            {t(language, 'calculatedFrom')}{' '}
            <strong className="font-bold">
              {displayDistance % 1 === 0 ? displayDistance : parseFloat(displayDistance.toFixed(2))} {displayDistanceUnit}
            </strong>{' '}
            {t(language, 'uiTxt')}{' '}
            <strong className="font-bold">
              {formatPace(displayPaceSecs).replace(/^0/, '')}{displayPaceUnit}
            </strong>
          </span>
          <span className="font-mono font-bold">
            {displaySpeed.toFixed(1)} {displaySpeedUnit}
          </span>
        </div>
      </div>
    );
  }

  if (activeCalculation === 'pace') {
    return (
      <div
        id="hero-result-pace"
        className={`rounded-2xl sm:rounded-3xl p-5 sm:p-6 transition-all ${currentTheme.container}`}
      >
        <div className="flex items-center gap-2.5">
          <div className={`w-9 h-9 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${currentTheme.iconContainer}`}>
            {paceOrSpeedMode === 'speed' ? (
              <Gauge className="w-5 h-5 stroke-[2.5]" />
            ) : (
              <Zap className="w-5 h-5 stroke-[2.5]" />
            )}
          </div>
          <div>
            <span className="text-xs sm:text-xs font-extrabold uppercase tracking-wider block opacity-95">
              {paceOrSpeedMode === 'speed' ? t(language, 'calculatedSpeed') : t(language, 'calculatedPace')}
            </span>
            <span className="text-xs opacity-75 font-medium">
              {t(language, 'uiRequiredPaceBasedOnDistance')}
            </span>
          </div>
        </div>

        <div className="my-4 text-center py-2.5 bg-white/60 dark:bg-black/25 rounded-2xl">
          <p className={`font-mono font-black text-3xl sm:text-4xl tracking-tight ${currentTheme.number}`}>
            {paceOrSpeedMode === 'speed' ? displaySpeed.toFixed(2) : formatPace(displayPaceSecs)}
          </p>
          <p className="text-xs font-bold mt-0.5 opacity-80">
            {paceOrSpeedMode === 'speed' ? displaySpeedUnit : displayPaceUnit}
          </p>
        </div>

        <div className="flex items-center justify-between text-xs opacity-90 font-medium px-1">
          <span>
            {t(language, 'calculatedFrom')}{' '}
            <strong className="font-bold">
              {displayDistance % 1 === 0 ? displayDistance : parseFloat(displayDistance.toFixed(2))} {displayDistanceUnit}
            </strong>{' '}
            {t(language, 'uiIn')}{' '}
            <strong className="font-bold">{formatTime(totalSeconds, true)}</strong>
          </span>
          <span className="font-mono font-bold px-2 py-0.5 rounded-lg bg-black/5 dark:bg-white/10">
            {paceOrSpeedMode === 'speed'
              ? `${formatPace(displayPaceSecs).replace(/^0/, '')}${displayPaceUnit}`
              : `${displaySpeed.toFixed(1)} ${displaySpeedUnit}`}
          </span>
        </div>
      </div>
    );
  }

  // Distance Result Card
  return (
    <div
      id="hero-result-distance"
      className={`rounded-2xl sm:rounded-3xl p-5 sm:p-6 transition-all ${currentTheme.container}`}
    >
      <div className="flex items-center gap-2.5">
        <div className={`w-9 h-9 rounded-xl sm:rounded-2xl flex items-center justify-center shrink-0 ${currentTheme.iconContainer}`}>
          <Navigation className="w-5 h-5 stroke-[2.5]" />
        </div>
        <div>
          <span className="text-xs sm:text-xs font-extrabold uppercase tracking-wider block opacity-95">
            {t(language, 'calculatedDistance')}
          </span>
          <span className="text-xs opacity-75 font-medium">
            {t(language, 'uiDistanceBasedOnTimePace')}
          </span>
        </div>
      </div>

      <div className="my-4 text-center py-2.5 bg-white/60 dark:bg-black/25 rounded-2xl">
        <p className={`font-mono font-black text-3xl sm:text-4xl tracking-tight ${currentTheme.number}`}>
          {displayDistance % 1 === 0 ? displayDistance : displayDistance.toFixed(2)}
        </p>
        <p className="text-xs font-bold mt-0.5 opacity-80">
          {displayDistanceUnit}
        </p>
      </div>

      <div className="flex items-center justify-between text-xs opacity-90 font-medium px-1">
        <span>
          {t(language, 'calculatedFrom')}{' '}
          <strong className="font-bold">{formatTime(totalSeconds, true)}</strong>{' '}
          {t(language, 'uiTxt')}{' '}
          <strong className="font-bold">
            {formatPace(displayPaceSecs).replace(/^0/, '')}{displayPaceUnit}
          </strong>
        </span>
        <span className="font-mono font-bold">
          {displaySpeed.toFixed(1)} {displaySpeedUnit}
        </span>
      </div>
    </div>
  );
}
